import type { ApiActivity, ApiTokenWithPrice, ApiWalletTransactionMetadata } from '../../../../api/types';

import { getIsActivityPendingForUser, parseTxId } from '../../../../util/activities';
import { toDecimal } from '../../../../util/decimals';

export type ActivityHistoryFilters = {
  query: string;
  direction: 'all' | 'incoming' | 'outgoing' | 'swap';
  status: 'all' | 'pending' | 'completed' | 'failed';
  asset: string;
  counterparty: string;
  category?: string;
  fromDate: string;
  toDate: string;
};

export const DEFAULT_HISTORY_FILTERS: ActivityHistoryFilters = Object.freeze({
  query: '', direction: 'all', status: 'all', asset: '', counterparty: '', category: '', fromDate: '', toDate: '',
});

export function normalizeHistorySearch(value: string) {
  return value.normalize('NFKC').trim().toLocaleLowerCase();
}

export function hasActivityHistoryFilters(filters: ActivityHistoryFilters) {
  return Boolean(filters.query.trim() || filters.direction !== 'all' || filters.status !== 'all'
    || filters.asset || filters.counterparty || filters.category || filters.fromDate || filters.toDate);
}

function dateBoundary(value: string, nextDay = false) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return undefined;
  if (nextDay) date.setDate(date.getDate() + 1);
  return date.getTime();
}

export function getActivityHistorySearchText(
  activity: ApiActivity,
  tokens: Record<string, ApiTokenWithPrice>,
  metadata?: ApiWalletTransactionMetadata,
  addressName?: string,
) {
  const isTransaction = activity.kind === 'transaction';
  const slugs = isTransaction ? [activity.slug] : [activity.from, activity.to];
  const values = [activity.id, metadata?.description, metadata?.counterparty?.name,
    metadata?.category?.name, addressName,
    ...slugs.flatMap((slug) => [slug, tokens[slug]?.name, tokens[slug]?.symbol])];
  if (isTransaction) {
    values.push(activity.fromAddress, activity.toAddress, activity.comment, activity.metadata?.name,
      parseTxId(activity.id).hash, toDecimal(activity.amount, tokens[activity.slug]?.decimals ?? 0));
  }
  return normalizeHistorySearch(values.filter(Boolean).join(' '));
}

export function createActivityHistoryFilter(
  filters: ActivityHistoryFilters, tokens: Record<string, ApiTokenWithPrice>,
) {
  const from = dateBoundary(filters.fromDate);
  const to = dateBoundary(filters.toDate, true);
  const counterparty = normalizeHistorySearch(filters.counterparty);
  const query = normalizeHistorySearch(filters.query);
  const words = query ? query.split(/\s+/) : [];
  return (
    activity: ApiActivity, metadata?: ApiWalletTransactionMetadata, addressName?: string, searchText?: string,
  ) => {
    const isTransaction = activity.kind === 'transaction';
    if (filters.direction === 'swap' && isTransaction) return false;
    if (filters.direction === 'incoming' && (!isTransaction || !activity.isIncoming)) return false;
    if (filters.direction === 'outgoing' && (!isTransaction || activity.isIncoming)) return false;
    if (filters.status === 'pending' && !getIsActivityPendingForUser(activity)) return false;
    if (filters.status === 'failed' && activity.status !== 'failed') return false;
    if (filters.status === 'completed' && (getIsActivityPendingForUser(activity) || activity.status === 'failed')) {
      return false;
    }
    if (filters.asset && (isTransaction
      ? activity.slug !== filters.asset : activity.from !== filters.asset && activity.to !== filters.asset)) {
      return false;
    }
    if (counterparty && metadata?.counterparty?.id !== filters.counterparty
      && normalizeHistorySearch(metadata?.counterparty?.name ?? '') !== counterparty) return false;
    if (filters.category && metadata?.category?.id !== filters.category) return false;

    if (from !== undefined && activity.timestamp < from) return false;
    if (to !== undefined && activity.timestamp >= to) return false;

    if (!query) return true;
    const text = searchText ?? getActivityHistorySearchText(activity, tokens, metadata, addressName);
    return words.every((word) => text.includes(word));
  };
}

export function matchesActivityHistoryFilters(
  activity: ApiActivity, filters: ActivityHistoryFilters, tokens: Record<string, ApiTokenWithPrice>,
  metadata?: ApiWalletTransactionMetadata, addressName?: string,
) {
  return createActivityHistoryFilter(filters, tokens)(activity, metadata, addressName);
}
